import { useCallback, useEffect, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { setToastRefToOpen } from "../../../functions/functions";
import { ContentProps, Toast } from "primereact/toast";

import updatePasswordByUserToken from "../../../resources/Api/authenticator/updatePasswordByUserToken";

import {
    Alert,
    Box,
    Button,
    DialogActions,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    Paper,
    TextField,
    Typography,
} from "@mui/material";

import validator from "validator";

import {
    LockPersonOutlined,
    SaveOutlined,
    VisibilityOffOutlined,
    VisibilityOutlined,
} from "@mui/icons-material";

/**
 * RecoveryPage - Component that renders a page using a temporary token to recovery password
 * @component
 */
const RecoveryPage = () => {
    // funcionalities states
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<
        "warn" | "success" | "info" | "error" | undefined
    >("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);

    const [token, setToken] = useState("");
    const [password, setPassword] = useState<string>("");
    const [secondPassword, setSecondPassword] = useState<string>("");
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
    const [isWeakPassword, setIsWeakPassword] = useState(false);

    // refs
    const toastRef = useRef<Toast>(null);
    
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const ref = toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible,
    ]);

    const getToken = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const tokenParam = params.get("token");
        
        if (tokenParam) {
            const decoder = new TextDecoder();
            const byteArray = Uint8Array.from(atob(tokenParam), (c) =>
                c.charCodeAt(0)
            );
            const decodedToken = decoder.decode(byteArray);
            setToken(decodedToken);
        } else {
            navigate("/");
        }
    }, [location.search, navigate]);

    useEffect(() => {
        getToken();
    }, [getToken]);

    const onUpdatePassword = useCallback(async () => {
        try {
            await updatePasswordByUserToken(token, password);
            setTextValidation("Senha alterada com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);
        } catch (error: any) {
            const { data } = error.response
                        
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                navigate("/")
            }, 3000);
        }
    }, [token, password, navigate]);

    const validate = useCallback((value: string) => {
        if (
            validator.isStrongPassword(value, {
                minLength: 8,
                minLowercase: 1,
                minUppercase: 1,
                minNumbers: 1,
                minSymbols: 1,
            })
        ) {
            setIsWeakPassword(false);
        } else {
            setIsWeakPassword(true);
        }
    }, []);

    useEffect(() => {
        if (password) {
            validate(password);
        }
    }, [password, validate]);

    const verifyBeforeUpdatePassword = useCallback(() => {
        const ref = toastRef;
        if (!isToastVisible) {
            setToastRefToOpen(
                ref,
                "Deseja realmente alterar o sua senha?",
                "Atenção!",
                statusMessage,
                {
                    content: (props: ContentProps) => (
                        <Box
                            className="flex flex-column align-items-left"
                            style={{ flex: "1" }}
                        >
                            <Typography>{props?.message.summary}</Typography>
                            <Box className="font-medium text-lg my-3 text-900">
                                {props?.message.detail}
                            </Box>
                            <Button
                                className="p-button-sm flex"
                                style={{ cursor: "pointer", color: "inherit" }}
                                onClick={() => {
                                    ref.current?.clear();
                                    setIsToastVisible(false);
                                    onUpdatePassword();
                                }}
                            >
                                Sim
                            </Button>
                            <Button
                                className="p-button-sm flex"
                                style={{ cursor: "pointer", color: "inherit" }}
                                onClick={() => {
                                    ref.current?.clear();
                                    setIsToastVisible(false);
                                }}
                            >
                                Não
                            </Button>
                        </Box>
                    ),
                },
                undefined,
                true
            );
            setIsToastVisible(true);
        }
    }, [onUpdatePassword, isToastVisible, statusMessage]);

    return (
        <>
            {!token && <></>}
            {token && <Box className="tw-flex tw-min-h-[100dvh] tw-w-full tw-items-center tw-justify-center tw-bg-slate-100 tw-p-4 dark:tw-bg-slate-950">
                <Toast
                    ref={toastRef}
                    position="bottom-center"
                    style={{ zIndex: 99999 }}
                    onShow={() => {
                        setIsToastVisible(true);

                        if (validationText) {
                            setTimeout(() => {
                                setFieldsIsValid(true);
                                setIsToastVisible(false);
                            }, 3000);
                        }
                    }}
                />
                <Paper
                    className="tw-w-full tw-max-w-2xl tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-none dark:tw-bg-slate-900"
                    elevation={0}
                    sx={{
                        fontFamily: "Prometo",
                        "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#006C77" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
                        "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700", borderWidth: 2 },
                    }}
                >
                    <Box className="tw-flex tw-items-center tw-gap-3 tw-bg-gtr-sidebar tw-px-5 tw-py-4 tw-text-white sm:tw-px-6">
                        <Box className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/10">
                            <LockPersonOutlined className="!tw-text-white" />
                        </Box>
                        <Box>
                            <Typography className="!tw-font-prometo !tw-text-lg !tw-font-bold !tw-text-white">
                                Redefinir senha
                            </Typography>
                            <Typography className="!tw-font-prometo !tw-text-xs !tw-text-white/70">
                                Cadastre uma nova senha de acesso
                            </Typography>
                        </Box>
                    </Box>

                    <Box className="tw-space-y-5 tw-p-5 sm:tw-p-6">
                        <Alert
                            className="!tw-rounded-xl !tw-border-0 !tw-bg-sky-50"
                            severity="info"
                        >
                            {
                                "Crie uma nova senha segura, ela deve conter pelo menos uma letra maiúscula, uma letra minúscula, números e um caracter especial. Ex: Gtr789#."
                            }
                        </Alert>
                        <FormControl
                            className="form-group"
                            style={{ display: "flex", width: "100%" }}
                        >
                            <InputLabel
                                id="demo-multiple-password-label"
                                shrink
                                style={{ fontFamily: "Prometo" }}
                                disabled={false}
                            >
                                Senha
                            </InputLabel>
                            <TextField
                                name="password"
                                autoComplete="off"
                                InputProps={{
                                    label: "Senha",
                                    notched: true,
                                    style: { fontFamily: "Prometo" },
                                }}
                                disabled={false}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                }}
                                type={isPasswordVisible ? "text" : "password"}
                                className="form-control form-control-login"
                                placeholder="Digite a senha"
                            />
                            <IconButton
                                aria-label="toggle password visibility"
                                className="visibility-password"
                                onClick={() => {
                                    setIsPasswordVisible(!isPasswordVisible);
                                }}
                                edge="end"
                                size="large"
                                disabled={false}
                                sx={{ color: "#52666a" }}
                            >
                                {isPasswordVisible ? (
                                    <VisibilityOffOutlined />
                                ) : (
                                    <VisibilityOutlined />
                                )}
                            </IconButton>
                        </FormControl>

                        <FormControl
                            className="form-group"
                            style={{ display: "flex", width: "100%" }}
                        >
                            <InputLabel
                                id="confirm-password-label"
                                shrink
                                style={{ fontFamily: "Prometo" }}
                            >
                                Confirmar senha
                            </InputLabel>
                            <TextField
                                name="confirmPassword"
                                autoComplete="off"
                                InputProps={{
                                    label: "Confirmar senha",
                                    notched: true,
                                    style: { fontFamily: "Prometo" },
                                }}
                                disabled={false}
                                value={secondPassword}
                                onChange={(e) => {
                                    setSecondPassword(e.target.value);
                                }}
                                type={isPasswordVisible ? "text" : "password"}
                                className="form-control form-control-login"
                                placeholder="Confirme a senha"
                            />
                            <Box className="tw-mt-2 tw-flex tw-min-h-6 tw-items-center">
                                <FormHelperText
                                    style={{
                                        color: isWeakPassword ? "red" : "green",
                                        display: "flex",
                                    }}
                                >
                                    {isWeakPassword &&
                                        password &&
                                        "A senha deve conter uma letra maiúscula, uma letra minúscula, números e pelo menos um carácter especial."}
                                    {!isWeakPassword && password && (
                                        <>
                                            <LockPersonOutlined style={{ marginRight: 5 }} />
                                            <Typography>Senha forte</Typography>
                                        </>
                                    )}
                                </FormHelperText>
                            </Box>
                        </FormControl>
                    </Box>
                    <DialogActions className="!tw-flex !tw-justify-end !tw-border-t !tw-border-slate-100 !tw-bg-slate-50 !tw-p-4 dark:!tw-border-slate-700 dark:!tw-bg-slate-900 sm:!tw-px-6">
                            <Button
                                type="button"
                                className="btn btn-primary btn-block login-button tw-shadow-sm tw-transition-all tw-duration-200"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={false}
                                onClick={() => {
                                    if (!password && !secondPassword) {
                                        setTextValidation("As senhas não podem ser vazias.");
                                        setStatusMessage("warn");
                                        setFieldsIsValid(false);
                                        return;
                                    }
                                    if (password !== secondPassword) {
                                        setTextValidation("As senhas devem ser iguais.");
                                        setStatusMessage("warn");
                                        setFieldsIsValid(false);
                                        return;
                                    } else {
                                        if (!isWeakPassword) {
                                            verifyBeforeUpdatePassword();
                                        }
                                    }
                                }}
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                    </DialogActions>
                </Paper>
            </Box>}
        </>
    );
};

export default RecoveryPage;
